import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, getApiErrorMessage } from '../lib/api';
import { resolveLocale } from '../lib/dateFormat';
import { Incident, ImpactDomain, WorkspaceGroup, IncidentAttachment, IncidentComment, User } from '../types';
import { useAuthStore, useUIStore } from '../store/index';
import { useConfirm } from '../components/ConfirmDialogProvider';

const IncidentDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { language } = useUIStore();
  const currentUser = useAuthStore((s) => s.user);
  const confirm = useConfirm();

  const [incident, setIncident] = useState<Incident | null>(null);
  const [domains, setDomains] = useState<ImpactDomain[]>([]);
  const [groups, setGroups] = useState<WorkspaceGroup[]>([]);
  const [members, setMembers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  // Incident edit — split into two independently-editable logical groups
  // (per-field edit would be 6 separate icons; grouped is 2) so a small
  // wording tweak doesn't force re-touching urgency/priority/domain too.
  // Group 1: title + descriptions.
  const [isEditingIncident, setIsEditingIncident] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editShortDescription, setEditShortDescription] = useState('');
  const [editDetailedDescription, setEditDetailedDescription] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  // Group 2: urgency + priority + domain.
  const [isEditingClassification, setIsEditingClassification] = useState(false);
  const [editUrgency, setEditUrgency] = useState(1);
  const [editPriority, setEditPriority] = useState(1);
  const [editDomainId, setEditDomainId] = useState('');
  const [editClassificationSaving, setEditClassificationSaving] = useState(false);
  const [editClassificationError, setEditClassificationError] = useState<string | null>(null);

  // Comments state
  const [comments, setComments] = useState<IncidentComment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [commentPosting, setCommentPosting] = useState(false);
  const [commentError, setCommentError] = useState<string | null>(null);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingCommentText, setEditingCommentText] = useState('');
  const [commentActionLoading, setCommentActionLoading] = useState<string | null>(null); // comment id being saved/deleted

  // Action form states
  const [action, setAction] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [startGroupId, setStartGroupId] = useState('');
  const [startNotes, setStartNotes] = useState('');
  const [closeRootCause, setCloseRootCause] = useState('');
  const [closeSolutionSteps, setCloseSolutionSteps] = useState('');
  const [reassignGroupId, setReassignGroupId] = useState('');
  const [reassignNote, setReassignNote] = useState('');

  // Attachment states
  const [attachments, setAttachments] = useState<IncidentAttachment[]>([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [attachmentDownloadLoading, setAttachmentDownloadLoading] = useState<string | null>(null); // attachment id being downloaded
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null); // attachment id being deleted
  const [attachError, setAttachError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNotFound(false);
    try {
      const [incRes, domainsRes, groupsRes, membersRes] = await Promise.all([
        api.get(`/incidents/${id}`),
        api.get('/workspace/domains'),
        api.get('/workspace/groups'),
        api.get('/workspace/members')
      ]);
      const incData = (incRes.data as any)?.data;
      if (!incData) {
        setNotFound(true);
        return;
      }
      setIncident(incData);

      // Attachments may come embedded in incident or we fetch separately
      if (Array.isArray((incData as any).attachments)) {
        setAttachments((incData as any).attachments);
      }

      const domData = (domainsRes.data as any)?.data;
      if (Array.isArray(domData)) setDomains(domData);

      const grpData = (groupsRes.data as any)?.data;
      if (Array.isArray(grpData)) setGroups(grpData);

      const memData = (membersRes.data as any)?.data;
      if (Array.isArray(memData)) setMembers(memData);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setNotFound(true);
      } else {
        setError(getApiErrorMessage(err, t('errors.general')));
      }
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    if (id) {
      fetchData();
    } else {
      setNotFound(true);
    }
  }, [id, fetchData]);

  const refreshIncident = async () => {
    try {
      const res = await api.get(`/incidents/${id}`);
      const data = (res.data as any)?.data;
      if (data) {
        setIncident(data);
        if (Array.isArray((data as any).attachments)) {
          setAttachments((data as any).attachments);
        }
      }
    } catch {
      // silent refresh failure
    }
  };

  const fetchAttachments = useCallback(async () => {
    setAttachmentsLoading(true);
    try {
      const res = await api.get(`/incidents/${id}/attachments`);
      const data = (res.data as any)?.data;
      if (Array.isArray(data)) {
        setAttachments(data);
      }
    } catch {
      // attachments fetch failure is non-critical
    } finally {
      setAttachmentsLoading(false);
    }
  }, [id]);

  // Also fetch attachments separately to ensure we have the latest
  useEffect(() => {
    if (id && incident) {
      fetchAttachments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, incident?.status, fetchAttachments]); // re-fetch when status changes

  const fetchComments = useCallback(async () => {
    setCommentsLoading(true);
    try {
      const res = await api.get(`/incidents/${id}/comments`);
      const data = (res.data as any)?.data;
      if (Array.isArray(data)) {
        setComments(data);
      }
    } catch {
      // comments fetch failure is non-critical
    } finally {
      setCommentsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchComments();
    }
  }, [id, fetchComments]);

  // ---- Action Handlers ----

  const handleStart = async (e: React.FormEvent) => {
    e.preventDefault();
    setActionLoading(true);
    setActionError(null);
    try {
      await api.put(`/incidents/${id}/start`, {
        assigned_group_id: startGroupId,
        investigation_notes: startNotes
      });
      await refreshIncident();
      setAction(null);
      setStartGroupId('');
      setStartNotes('');
    } catch (err: any) {
      setActionError(getApiErrorMessage(err, t('errors.general')));
    } finally {
      setActionLoading(false);
    }
  };

  const handleClose = async (e: React.FormEvent) => {
    e.preventDefault();
    setActionLoading(true);
    setActionError(null);
    try {
      await api.put(`/incidents/${id}/close`, {
        root_cause: closeRootCause,
        solution_steps: closeSolutionSteps
      });
      await refreshIncident();
      setAction(null);
      setCloseRootCause('');
      setCloseSolutionSteps('');
    } catch (err: any) {
      setActionError(getApiErrorMessage(err, t('errors.general')));
    } finally {
      setActionLoading(false);
    }
  };

  const handleReassign = async (e: React.FormEvent) => {
    e.preventDefault();
    setActionLoading(true);
    setActionError(null);
    try {
      await api.put(`/incidents/${id}/reassign`, {
        assigned_group_id: reassignGroupId,
        note: reassignNote.trim()
      });
      await refreshIncident();
      await fetchComments();
      setAction(null);
      setReassignGroupId('');
      setReassignNote('');
    } catch (err: any) {
      setActionError(getApiErrorMessage(err, t('errors.general')));
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkProblem = async () => {
    if (!(await confirm(t('incidents.detail.confirmations.markProblem'), { variant: 'danger' }))) return;
    setActionLoading(true);
    setActionError(null);
    try {
      await api.put(`/incidents/${id}/mark-problem`);
      await refreshIncident();
    } catch (err: any) {
      setActionError(getApiErrorMessage(err, t('errors.general')));
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!(await confirm(t('incidents.detail.confirmations.confirmSolution')))) return;
    setActionLoading(true);
    setActionError(null);
    try {
      await api.put(`/incidents/${id}/confirm`);
      await refreshIncident();
    } catch (err: any) {
      setActionError(getApiErrorMessage(err, t('errors.general')));
    } finally {
      setActionLoading(false);
    }
  };

  // ---- Incident Edit Handlers ----

  const canEditIncident =
    !!currentUser &&
    !!incident &&
    (currentUser.role === 'superadmin' || currentUser.role === 'manager' || incident.reported_by === currentUser.id);

  const handleStartEditIncident = () => {
    if (!incident) return;
    setEditTitle(incident.title);
    setEditShortDescription(incident.short_description);
    setEditDetailedDescription(incident.detailed_description);
    setEditError(null);
    setIsEditingIncident(true);
  };

  const handleCancelEditIncident = () => {
    setIsEditingIncident(false);
    setEditError(null);
  };

  const handleSaveEditIncident = async (e: React.FormEvent) => {
    e.preventDefault();
    setEditSaving(true);
    setEditError(null);
    try {
      await api.patch(`/incidents/${id}`, {
        title: editTitle,
        short_description: editShortDescription,
        detailed_description: editDetailedDescription
      });
      await refreshIncident();
      setIsEditingIncident(false);
    } catch (err: any) {
      setEditError(getApiErrorMessage(err, t('errors.general')));
    } finally {
      setEditSaving(false);
    }
  };

  const handleStartEditClassification = () => {
    if (!incident) return;
    setEditUrgency(incident.urgency);
    setEditPriority(incident.priority);
    setEditDomainId(incident.domain_id || '');
    setEditClassificationError(null);
    setIsEditingClassification(true);
  };

  const handleCancelEditClassification = () => {
    setIsEditingClassification(false);
    setEditClassificationError(null);
  };

  const handleSaveEditClassification = async (e: React.FormEvent) => {
    e.preventDefault();
    setEditClassificationSaving(true);
    setEditClassificationError(null);
    try {
      await api.patch(`/incidents/${id}`, {
        urgency: editUrgency,
        priority: editPriority,
        domain_id: editDomainId || null
      });
      await refreshIncident();
      setIsEditingClassification(false);
    } catch (err: any) {
      setEditClassificationError(getApiErrorMessage(err, t('errors.general')));
    } finally {
      setEditClassificationSaving(false);
    }
  };

  // ---- Comment Handlers ----

  const getAuthorName = (authorId: string): string => {
    const m = members.find((m) => m.id === authorId);
    return m ? m.name : authorId;
  };

  const canEditComment = (c: IncidentComment): boolean =>
    !!currentUser &&
    (c.author_id === currentUser.id || currentUser.role === 'superadmin' || currentUser.role === 'manager');

  const canDeleteComment = (c: IncidentComment): boolean =>
    !!currentUser &&
    (c.author_id === currentUser.id || currentUser.role === 'superadmin' || currentUser.role === 'manager');

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    setCommentPosting(true);
    setCommentError(null);
    try {
      const res = await api.post(`/incidents/${id}/comments`, { message: commentText.trim() });
      const created = (res.data as any)?.data;
      if (created) {
        setComments((prev) => [...prev, created]);
      }
      setCommentText('');
    } catch (err: any) {
      setCommentError(getApiErrorMessage(err, t('errors.general')));
    } finally {
      setCommentPosting(false);
    }
  };

  const handleStartEditComment = (c: IncidentComment) => {
    setEditingCommentId(c.id);
    setEditingCommentText(c.message);
    setCommentError(null);
  };

  const handleCancelEditComment = () => {
    setEditingCommentId(null);
    setEditingCommentText('');
  };

  const handleSaveEditComment = async (commentId: string) => {
    if (!editingCommentText.trim()) return;
    setCommentActionLoading(commentId);
    setCommentError(null);
    try {
      const res = await api.patch(`/incidents/${id}/comments/${commentId}`, { message: editingCommentText.trim() });
      const updated = (res.data as any)?.data;
      if (updated) {
        setComments((prev) => prev.map((c) => (c.id === commentId ? updated : c)));
      }
      setEditingCommentId(null);
      setEditingCommentText('');
    } catch (err: any) {
      setCommentError(getApiErrorMessage(err, t('errors.general')));
    } finally {
      setCommentActionLoading(null);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!(await confirm(t('incidents.detail.comments.deleteConfirm'), { variant: 'danger' }))) return;
    setCommentActionLoading(commentId);
    setCommentError(null);
    try {
      await api.delete(`/incidents/${id}/comments/${commentId}`);
      setComments((prev) => prev.filter((c) => c.id !== commentId));
    } catch (err: any) {
      setCommentError(getApiErrorMessage(err, t('errors.general')));
    } finally {
      setCommentActionLoading(null);
    }
  };

  // ---- Attachment Handlers ----

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadLoading(true);
    setAttachError(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      await api.post(`/incidents/${id}/attachments`, formData);
      await fetchAttachments();
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err: any) {
      if (axios.isAxiosError(err) && err.response?.status === 409 && err.response.data?.error?.code === 'QUOTA_EXCEEDED') {
        const { candidates, neededBytes } = err.response.data.data;
        const names = candidates.map((c: any) => c.original_name).join(', ');
        const confirmMsg = t('fileQuota.confirmPrompt', { count: candidates.length, names, size: formatSize(neededBytes) });
        if (await confirm(confirmMsg)) {
          try {
            const retryFormData = new FormData();
            retryFormData.append('file', file);
            retryFormData.append('confirmEviction', 'true');
            await api.post(`/incidents/${id}/attachments`, retryFormData);
            await fetchAttachments();
            if (fileInputRef.current) fileInputRef.current.value = '';
          } catch (retryErr: any) {
            setAttachError(getApiErrorMessage(retryErr, t('errors.general')));
          }
        } else if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        setAttachError(getApiErrorMessage(err, t('errors.general')));
      }
    } finally {
      setUploadLoading(false);
    }
  };

  // New (2026-08-13, /uploads auth fix): incident attachments previously
  // had no way to view/download them at all in this UI — only list +
  // delete. Mirrors handleDownloadPdf's blob-fetch pattern below.
  const handleDownloadAttachment = async (attachmentId: string, originalName: string) => {
    setAttachmentDownloadLoading(attachmentId);
    setAttachError(null);
    try {
      const res = await api.get(`/incidents/${id}/attachments/${attachmentId}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = originalName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setAttachError(getApiErrorMessage(err, t('errors.general')));
    } finally {
      setAttachmentDownloadLoading(null);
    }
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    if (!(await confirm(t('incidents.detail.confirmations.deleteFile'), { variant: 'danger' }))) return;
    setDeleteLoading(attachmentId);
    setAttachError(null);
    try {
      await api.delete(`/incidents/${id}/attachments/${attachmentId}`);
      setAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
    } catch (err: any) {
      setAttachError(getApiErrorMessage(err, t('errors.general')));
    } finally {
      setDeleteLoading(null);
    }
  };

  const handleDownloadPdf = async () => {
    setDownloadLoading(true);
    setAttachError(null);
    try {
      const res = await api.get(`/incidents/${id}/report.pdf`, {
        responseType: 'blob'
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${incident?.project_key ?? 'INC'}-${incident?.incident_number ?? ''}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setAttachError(getApiErrorMessage(err, t('errors.general')));
    } finally {
      setDownloadLoading(false);
    }
  };

  // ---- Helpers ----

  const getStatusBadge = (status: string) => {
    const config: Record<string, { color: string; label: string }> = {
      open: { color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300', label: t('incidents.detail.status.open') },
      in_progress: { color: 'bg-yellow-100 text-yellow-800 dark:bg-amber-500/15 dark:text-amber-300 dark:ring-1 dark:ring-inset dark:ring-amber-500/30', label: t('incidents.detail.status.inProgress') },
      closed: { color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300', label: t('incidents.detail.status.closed') },
      problem: { color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300', label: t('incidents.detail.status.problem') }
    };
    const c = config[status] || { color: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300', label: status };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${c.color}`}>
        {c.label}
      </span>
    );
  };

  const getSlaBadge = (sla: string | null | undefined) => {
    if (!sla) return null;
    const config: Record<string, { color: string; label: string }> = {
      on_track: { color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300', label: t('incidents.detail.sla.onTrack') },
      breached: { color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300', label: t('incidents.detail.sla.breached') },
      pending: { color: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300', label: t('incidents.detail.sla.pending') }
    };
    const c = config[sla] || { color: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300', label: sla };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${c.color}`}>
        {c.label}
      </span>
    );
  };

  const renderStars = (value: number) => {
    const stars = [];
    for (let i = 1; i <= 4; i++) {
      const filled = i <= value;
      stars.push(
        <span key={i} className={`p-1 ${filled ? 'text-yellow-400' : 'text-gray-300'}`}>
          <svg className="w-5 h-5" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
          </svg>
        </span>
      );
    }
    return stars;
  };

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString(resolveLocale(language), {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const getDomainName = (domainId: string | null): string => {
    if (!domainId) return '-';
    const d = domains.find((d) => d.id === domainId);
    return d ? d.name : domainId;
  };

  const getGroupName = (groupId: string | null): string => {
    if (!groupId) return '-';
    const g = groups.find((g) => g.id === groupId);
    return g ? g.name : groupId;
  };

  const formatSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    return `${(bytes / 1024).toFixed(1)} KB`;
  };

  const canUpload = incident && (incident.status === 'open' || incident.status === 'in_progress' || incident.status === 'problem');

  // ---- Render ----

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
        <span className="ml-3 text-gray-600 dark:text-gray-400">{t('incidents.detail.loading')}</span>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="space-y-6">
        <div>
          <Link to="/incidents" className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-sm">&larr; {t('incidents.detail.backToList')}</Link>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-8 text-center">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">{t('incidents.detail.notFound')}</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{t('incidents.detail.notFoundDesc')}</p>
          <Link to="/incidents" className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline">{t('incidents.detail.backToList')}</Link>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <Link to="/incidents" className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-sm">&larr; {t('incidents.detail.backToList')}</Link>
        </div>
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-900/40 text-yellow-800 dark:text-yellow-400 px-4 py-3 rounded-lg">
          {error}
        </div>
      </div>
    );
  }

  if (!incident) return null;

  const isClosedUnconfirmed = incident.status === 'closed' && !incident.confirmed_at;

  return (
    <div className="space-y-4">
      {/* Back link */}
      <div>
        <Link to="/incidents" className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-sm">&larr; {t('incidents.detail.backToList')}</Link>
      </div>

      {/* Header: key, title, status/SLA, urgency/priority, and actions — one compact card */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {incident.project_key}-{incident.incident_number}
              </h1>
              <p className="text-base text-gray-700 dark:text-gray-300 mt-0.5">{incident.title}</p>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                <span className="flex items-center gap-1">{t('incidents.detail.fields.urgency')}: <span className="flex">{renderStars(incident.urgency)}</span></span>
                <span className="flex items-center gap-1">{t('incidents.detail.fields.priority')}: <span className="flex">{renderStars(incident.priority)}</span></span>
                {canEditIncident && !isEditingClassification && (
                  <button
                    onClick={handleStartEditClassification}
                    className="p-1 text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                    title={t('incidents.detail.edit.button') as string}
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                )}
              </div>
              {isEditingClassification && (
                <form onSubmit={handleSaveEditClassification} className="mt-2 p-3 bg-gray-50 dark:bg-gray-900/40 rounded-lg border border-gray-200 dark:border-gray-700 space-y-3 max-w-sm">
                  {editClassificationError && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/40 text-red-700 dark:text-red-400 px-3 py-2 rounded text-xs">
                      {editClassificationError}
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{t('incidents.detail.fields.urgency')}</label>
                      <select
                        value={editUrgency}
                        onChange={(e) => setEditUrgency(Number(e.target.value))}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 rounded-lg"
                      >
                        {[1, 2, 3, 4].map((n) => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{t('incidents.detail.fields.priority')}</label>
                      <select
                        value={editPriority}
                        onChange={(e) => setEditPriority(Number(e.target.value))}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 rounded-lg"
                      >
                        {[1, 2, 3, 4].map((n) => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{t('incidents.detail.fields.domain')}</label>
                    <select
                      value={editDomainId}
                      onChange={(e) => setEditDomainId(e.target.value)}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 rounded-lg"
                    >
                      <option value="">-</option>
                      {domains.map((d) => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="submit"
                      disabled={editClassificationSaving}
                      className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg transition-colors"
                    >
                      {editClassificationSaving ? t('incidents.detail.actions.processing') : t('incidents.detail.edit.save')}
                    </button>
                    <button
                      type="button"
                      onClick={handleCancelEditClassification}
                      className="px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                    >
                      {t('incidents.detail.edit.cancel')}
                    </button>
                  </div>
                </form>
              )}
            </div>
            <div className="flex items-center gap-2">
              {getStatusBadge(incident.status)}
              {getSlaBadge(incident.sla_status)}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="px-5 py-4">
          {actionError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/40 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg text-sm mb-4">
              {actionError}
            </div>
          )}

          {incident.status === 'open' && (
            <div>
              {action !== 'start' ? (
                <button
                  onClick={() => { setAction('start'); setActionError(null); }}
                  className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  disabled={actionLoading}
                >
                  {t('incidents.detail.actions.start')}
                </button>
              ) : (
                <form onSubmit={handleStart} className="space-y-4 max-w-lg">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('incidents.detail.actions.assignedGroup')} *</label>
                    <select
                      required
                      value={startGroupId}
                      onChange={(e) => setStartGroupId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">{t('incidents.detail.actions.selectGroup')}</option>
                      {groups.map((g) => (
                        <option key={g.id} value={g.id}>{g.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('incidents.detail.actions.investigationNotes')} *</label>
                    <textarea
                      required
                      rows={3}
                      value={startNotes}
                      onChange={(e) => setStartNotes(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder={t('incidents.detail.actions.investigationNotesPlaceholder')}
                    />
                  </div>
                  <div className="flex gap-3">
                    <button
                      type="submit"
                      disabled={actionLoading}
                      className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      {actionLoading ? t('incidents.detail.actions.processing') : t('incidents.detail.actions.start')}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setAction(null); setActionError(null); }}
                      className="px-5 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      {t('incidents.detail.actions.cancel')}
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

          {(incident.status === 'in_progress' || incident.status === 'problem') && (
            <div>
              {!action && (
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => { setAction('close'); setActionError(null); }}
                    className="px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    disabled={actionLoading}
                  >
                    {t('incidents.detail.actions.close')}
                  </button>
                  <button
                    onClick={() => { setAction('reassign'); setActionError(null); }}
                    className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    disabled={actionLoading}
                  >
                    {t('incidents.detail.actions.reassign')}
                  </button>
                  {incident.status === 'in_progress' && (
                    <button
                      onClick={handleMarkProblem}
                      disabled={actionLoading}
                      className="px-5 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                    >
                      {actionLoading ? t('incidents.detail.actions.processing') : t('incidents.detail.actions.markProblem')}
                    </button>
                  )}
                </div>
              )}

              {action === 'close' && (
                <form onSubmit={handleClose} className="space-y-4 max-w-lg">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('incidents.detail.actions.rootCause')} *</label>
                    <textarea
                      required
                      rows={3}
                      value={closeRootCause}
                      onChange={(e) => setCloseRootCause(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder={t('incidents.detail.actions.rootCausePlaceholder')}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('incidents.detail.actions.solutionSteps')} *</label>
                    <textarea
                      required
                      rows={3}
                      value={closeSolutionSteps}
                      onChange={(e) => setCloseSolutionSteps(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder={t('incidents.detail.actions.solutionStepsPlaceholder')}
                    />
                  </div>
                  <div className="flex gap-3">
                    <button
                      type="submit"
                      disabled={actionLoading}
                      className="px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                    >
                      {actionLoading ? t('incidents.detail.actions.processing') : t('incidents.detail.actions.close')}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setAction(null); setActionError(null); }}
                      className="px-5 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      {t('incidents.detail.actions.cancel')}
                    </button>
                  </div>
                </form>
              )}

              {action === 'reassign' && (
                <form onSubmit={handleReassign} className="space-y-4 max-w-lg">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('incidents.detail.actions.newGroup')} *</label>
                    <select
                      required
                      value={reassignGroupId}
                      onChange={(e) => setReassignGroupId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">{t('incidents.detail.actions.selectGroup')}</option>
                      {groups.map((g) => (
                        <option key={g.id} value={g.id}>{g.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('incidents.detail.actions.reassignNote')} *</label>
                    <textarea
                      required
                      minLength={2}
                      rows={2}
                      value={reassignNote}
                      onChange={(e) => setReassignNote(e.target.value)}
                      placeholder={t('incidents.detail.actions.reassignNotePlaceholder') as string}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div className="flex gap-3">
                    <button
                      type="submit"
                      disabled={actionLoading}
                      className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      {actionLoading ? t('incidents.detail.actions.processing') : t('incidents.detail.actions.assign')}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setAction(null); setActionError(null); setReassignNote(''); }}
                      className="px-5 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      {t('incidents.detail.actions.cancel')}
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

          {isClosedUnconfirmed && (
            <div>
              <button
                onClick={handleConfirm}
                disabled={actionLoading}
                className="px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {actionLoading ? t('incidents.detail.actions.processing') : t('incidents.detail.actions.confirm')}
              </button>
            </div>
          )}

        </div>
      </div>

      {/* Two-column body: main content (descriptions) + compact sidebar (details/attachments) */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-4 items-start">
        <div className="space-y-4 min-w-0">
          {/* Descriptions (or the edit form, in place, when editing) */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
            <div className="px-5 py-4 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{t('common.description')}</h2>
                {canEditIncident && !isEditingIncident && (
                  <button
                    onClick={handleStartEditIncident}
                    className="p-1 text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                    title={t('incidents.detail.edit.button') as string}
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                )}
              </div>
              {isEditingIncident ? (
                <form onSubmit={handleSaveEditIncident} className="space-y-4">
                  {editError && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/40 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg text-sm">
                      {editError}
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('common.title')} *</label>
                    <input
                      required
                      type="text"
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('incidents.detail.fields.shortDescription')} *</label>
                    <input
                      required
                      type="text"
                      value={editShortDescription}
                      onChange={(e) => setEditShortDescription(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('incidents.detail.fields.detailedDescription')} *</label>
                    <textarea
                      required
                      rows={4}
                      value={editDetailedDescription}
                      onChange={(e) => setEditDetailedDescription(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div className="flex gap-3">
                    <button
                      type="submit"
                      disabled={editSaving}
                      className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      {editSaving ? t('incidents.detail.actions.processing') : t('incidents.detail.edit.save')}
                    </button>
                    <button
                      type="button"
                      onClick={handleCancelEditIncident}
                      className="px-5 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      {t('incidents.detail.edit.cancel')}
                    </button>
                  </div>
                </form>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('incidents.detail.fields.shortDescription')}</label>
                    <p className="text-sm text-gray-900 dark:text-gray-100">{incident.short_description}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('incidents.detail.fields.detailedDescription')}</label>
                    <p className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap">{incident.detailed_description}</p>
                  </div>

                  {incident.investigation_notes && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('incidents.detail.fields.investigationNotes')}</label>
                      <p className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap">{incident.investigation_notes}</p>
                    </div>
                  )}
                  {incident.root_cause && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('incidents.detail.fields.rootCause')}</label>
                      <p className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap">{incident.root_cause}</p>
                    </div>
                  )}
                  {incident.solution_steps && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('incidents.detail.fields.solutionSteps')}</label>
                      <p className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap">{incident.solution_steps}</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Comments */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
            <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
              <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('incidents.detail.comments.title')}</h2>
            </div>
            <div className="px-5 py-4 space-y-3">
              {commentError && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/40 text-red-700 dark:text-red-400 px-3 py-2 rounded-lg text-sm">
                  {commentError}
                </div>
              )}

              {commentsLoading && comments.length === 0 && (
                <div className="text-center py-4 text-gray-500 dark:text-gray-400 text-sm">{t('incidents.detail.comments.loading')}</div>
              )}

              {!commentsLoading && comments.length === 0 && (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">{t('incidents.detail.comments.noComments')}</p>
              )}

              {comments.length > 0 && (
                <div className="space-y-3">
                  {comments.map((c) => (
                    <div key={c.id} className="bg-gray-50 dark:bg-gray-900/50 rounded-lg px-3 py-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <span className="text-xs font-medium text-gray-900 dark:text-gray-100">{getAuthorName(c.author_id)}</span>
                          {c.comment_type === 'reassign' && (
                            <span className="text-xs font-medium text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 rounded px-1.5 py-0.5 ml-2">
                              {t('incidents.detail.comments.reassignBadge')}
                            </span>
                          )}
                          <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                            {formatDate(c.created_at)}{c.updated_at ? ` ${t('incidents.detail.comments.edited')}` : ''}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {canEditComment(c) && editingCommentId !== c.id && (
                            <button
                              onClick={() => handleStartEditComment(c)}
                              className="p-1 text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                              title={t('incidents.detail.comments.edit') as string}
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                          )}
                          {canDeleteComment(c) && editingCommentId !== c.id && (
                            <button
                              onClick={() => handleDeleteComment(c.id)}
                              disabled={commentActionLoading === c.id}
                              className="p-1 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors disabled:opacity-50"
                              title={t('incidents.detail.comments.delete') as string}
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>

                      {editingCommentId === c.id ? (
                        <div className="mt-2 space-y-2">
                          <textarea
                            rows={2}
                            value={editingCommentText}
                            onChange={(e) => setEditingCommentText(e.target.value)}
                            className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleSaveEditComment(c.id)}
                              disabled={commentActionLoading === c.id}
                              className="px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 transition-colors disabled:opacity-50"
                            >
                              {t('incidents.detail.comments.save')}
                            </button>
                            <button
                              onClick={handleCancelEditComment}
                              className="px-3 py-1 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded text-xs hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                              {t('incidents.detail.comments.cancel')}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap mt-1">{c.message}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <form onSubmit={handleAddComment} className="flex gap-2 pt-2">
                <input
                  type="text"
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder={t('incidents.detail.comments.placeholder') as string}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <button
                  type="submit"
                  disabled={commentPosting || !commentText.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 text-sm"
                >
                  {commentPosting ? t('incidents.detail.comments.posting') : t('incidents.detail.comments.post')}
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Sidebar: details + attachments/PDF */}
        <div className="space-y-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
            <div className="px-4 py-4 space-y-3">
              <div>
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('incidents.detail.fields.domain')}</span>
                <p className="text-sm text-gray-900 dark:text-gray-100">{getDomainName(incident.domain_id)}</p>
              </div>
              <div>
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('incidents.detail.fields.assignedGroup')}</span>
                <p className="text-sm text-gray-900 dark:text-gray-100">{getGroupName(incident.assigned_group_id)}</p>
              </div>
              <div>
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('incidents.detail.fields.createdAt')}</span>
                <p className="text-sm text-gray-900 dark:text-gray-100">{formatDate(incident.created_at)}</p>
              </div>
              <div>
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('incidents.detail.fields.slaDeadline')}</span>
                <p className="text-sm text-gray-900 dark:text-gray-100">{formatDate(incident.sla_deadline)}</p>
              </div>
              <div>
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('incidents.detail.fields.reportedBy')}</span>
                <p className="text-sm text-gray-900 dark:text-gray-100">{incident.reported_by ? getAuthorName(incident.reported_by) : '-'}</p>
              </div>
              <div>
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('incidents.detail.fields.resolvedAt')}</span>
                <p className="text-sm text-gray-900 dark:text-gray-100">{formatDate(incident.resolved_at)}</p>
              </div>
              {incident.closed_at && (
                <div>
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('incidents.detail.fields.closedAt')}</span>
                  <p className="text-sm text-gray-900 dark:text-gray-100">{formatDate(incident.closed_at)}</p>
                </div>
              )}
              {incident.confirmed_at && (
                <div>
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('incidents.detail.fields.confirmedAt')}</span>
                  <p className="text-sm text-gray-900 dark:text-gray-100">{formatDate(incident.confirmed_at)}</p>
                </div>
              )}
              {incident.confirmed_by && (
                <div>
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('incidents.detail.fields.confirmedBy')}</span>
                  <p className="text-sm text-gray-900 dark:text-gray-100">{getAuthorName(incident.confirmed_by)}</p>
                </div>
              )}
            </div>
          </div>

          {/* Attachments & PDF Download card */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
            <div className="px-4 py-4">
              <div className="flex flex-col gap-2 mb-3">
                <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('incidents.detail.attachments.title')}</h2>
                <button
                  onClick={handleDownloadPdf}
                  disabled={downloadLoading}
                  className="w-full px-2.5 py-1.5 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5 text-xs"
                  title={downloadLoading ? t('incidents.detail.attachments.downloading') as string : t('incidents.detail.attachments.downloadPdf') as string}
                >
                  {downloadLoading ? (
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  )}
                  {t('incidents.detail.attachments.downloadPdf')}
                </button>
              </div>

              {attachError && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/40 text-red-700 dark:text-red-400 px-3 py-2 rounded-lg text-sm mb-3">
                  {attachError}
                </div>
              )}

              {/* Upload area - only when open or in_progress */}
              {canUpload && (
                <div className="mb-3 p-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
                  <label className="flex flex-col items-center cursor-pointer">
                    <svg className="w-6 h-6 text-gray-400 dark:text-gray-500 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <span className="text-xs text-gray-600 dark:text-gray-400 mb-0.5 text-center">{t('incidents.detail.attachments.uploadPrompt')}</span>
                    <span className="text-xs text-gray-400 dark:text-gray-500">{t('incidents.detail.attachments.maxSize')}</span>
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="hidden"
                      onChange={handleUpload}
                      disabled={uploadLoading}
                    />
                  </label>
                  {uploadLoading && (
                    <div className="flex items-center justify-center mt-2 text-xs text-blue-600">
                      <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      {t('incidents.detail.attachments.uploading')}
                    </div>
                  )}
                </div>
              )}

              {/* Attachments list */}
              {attachmentsLoading && attachments.length === 0 && (
                <div className="text-center py-4 text-gray-500 dark:text-gray-400 text-sm">{t('incidents.detail.loading')}</div>
              )}

              {!attachmentsLoading && attachments.length === 0 && !canUpload && (
                <div className="text-center py-4 text-gray-500 dark:text-gray-400 text-sm">{t('incidents.detail.attachments.noFiles')}</div>
              )}

              {attachments.length > 0 && (
                <div className="space-y-2">
                  {attachments.map((att) => (
                    <div key={att.id} className="flex items-center justify-between bg-gray-50 dark:bg-gray-900/50 rounded-lg px-3 py-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <svg className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate">{att.original_name}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {formatSize(att.size_bytes)} — {formatDate(att.uploaded_at)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                        <button
                          onClick={() => handleDownloadAttachment(att.id, att.original_name)}
                          disabled={attachmentDownloadLoading === att.id}
                          className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors disabled:opacity-50"
                          title={t('incidents.detail.attachments.download')}
                        >
                          {attachmentDownloadLoading === att.id ? (
                            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          )}
                        </button>
                        <button
                          onClick={() => handleDeleteAttachment(att.id)}
                          disabled={deleteLoading === att.id}
                          className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors disabled:opacity-50"
                          title={t('incidents.detail.attachments.delete')}
                        >
                          {deleteLoading === att.id ? (
                            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IncidentDetails;
